import PropTypes from 'prop-types';
import classes from './PinInput.module.scss';
import { useRef, useImperativeHandle, useCallback } from 'react';
import clsx from 'clsx';
import { SIZE_INPUT, VARIANT_INPUT } from '../../utils/constants';
import { createLetter, isAccent } from '../../utils/functions';
import { useGameSounds, NAME_SOUND_HOVER } from '@educaplay/core';
import { useSelector } from 'react-redux';

export function PinInput(props){
    const { letters, onChange, onNavigateNext, onNavigatePrevious, onNavigateDeletePrevious,
        onPaste, variant, size, disabled, variantValidation, ref } = props;

    const inputElements = useRef({});
    const gameSounds = useGameSounds();
    const isAnimating =  useSelector(state => state.game.isAnimating);

    const deleteLetter = useCallback((index, options = {}) => {
        gameSounds.play('move');
        const newLetters = [...letters];
        newLetters[index] = createLetter('');
        onChange(newLetters);
        if (options.autoFocus) {

            inputElements.current[index]?.focus();
        }
    }, [letters, onChange, gameSounds])

    const getNextIndexAvailable = useCallback((index = -1) => {
        for (let i = index + 1; i < letters.length; i++) {
            if (!letters[i].disabled) return i;
        }
        return null;
    }, [letters])

    const getPrevIndexAvailable = useCallback((index) => {
        for (let i = index - 1; i >= 0; i--) {
            if (!letters[i].disabled) return i;
        }
        return null;
    }, [letters])
    
    const handleDelete = useCallback((index) => {
        const prevVal = letters[index];
        if (prevVal.value) {
            deleteLetter(index);
            return;
        }

        if (index === 0) {
            onNavigateDeletePrevious();
            return;
        }

        const indexToDelete = getPrevIndexAvailable(index);
        if (indexToDelete === null) {
            onNavigateDeletePrevious();
            return;
        }

        deleteLetter(indexToDelete, { autoFocus: true });
    }, [letters, onNavigateDeletePrevious, getPrevIndexAvailable, deleteLetter])

    useImperativeHandle(ref, () => ({
        focusStart: () => {
            const nextIndex = getNextIndexAvailable();
            if (nextIndex === null) {
                onNavigateNext();
            } else {
                inputElements.current[nextIndex].focus();
            }
        },
        focusEnd: () => {
            const prevIndex = getPrevIndexAvailable(letters.length);
            if (prevIndex === null) {
                onNavigatePrevious();
            } else {
                inputElements.current[prevIndex].focus();
            }
        },
        focusIndex: (index) => {
            inputElements.current[index].focus();
        },
        deleteLastLetter: () => {
            if(!Array.isArray(letters)) return;

            if(!letters[letters.length -1]) return;
            const indexToDelete = letters[letters.length -1].disabled ? getPrevIndexAvailable(letters.length -1) : letters.length -1;
            deleteLetter(indexToDelete, { autoFocus: true });
        }
    }), [getNextIndexAvailable, getPrevIndexAvailable, onNavigateNext, onNavigatePrevious, letters, deleteLetter])

    const handleKeyDown = (event, index) => {
        if (event.key === ' ') {
            event.preventDefault();
            return;
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            if (index === letters.length - 1) {
                onNavigateNext();
                return;
            }
            const nextIndex = getNextIndexAvailable(index);
            if (nextIndex === null) {
                onNavigateNext();
            } else {
                inputElements.current[nextIndex].focus();
            }
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            if (index === 0) {
                onNavigatePrevious();
                return;
            }
            const prevIndex = getPrevIndexAvailable(index);
            if (prevIndex === null) {
                onNavigatePrevious();
            } else {
                inputElements.current[prevIndex].focus();
            }
        }

        if (event.key === 'Backspace') {
            event.preventDefault();
            handleDelete(index);
        }
    }

    const handleOnChange = (event, index) => {
        gameSounds.play('write');
        const currentValue = event.target.value;

        const trimmed = currentValue.trim();
        const letter = trimmed.length > 0 ? trimmed.charAt(trimmed.length - 1) : '';        
        const normalizedValue = letter.toUpperCase();

        const newLetters = [...letters];
        newLetters[index] = createLetter(normalizedValue);
        onChange(newLetters);

        if (isAccent(normalizedValue)) {
            return;
        }

        const nextIndex = getNextIndexAvailable(index);
        if (nextIndex === null) {
            onNavigateNext();
        } else {
            inputElements.current[nextIndex].focus();
        }
    }

    const handleFocus = (index) => {
        inputElements.current[index].focus();
        inputElements.current[index].select();
        gameSounds.play('goodActive');
    }

    const classNameInput = {
        [classes.input]: true,
        [classes.transparent]: variant === VARIANT_INPUT.TRANSPARENT,
        [classes.small]: size === SIZE_INPUT.SMALL,
        [classes.correct]: variantValidation === VARIANT_INPUT.CORRECT,
        [classes.incorrect]: variantValidation === VARIANT_INPUT.INCORRECT,
    }

    return (
        <div className={clsx(classes.inputWrapper, size === SIZE_INPUT.SMALL && classes.small)}>
            {letters.map((letter, index) => {

                const inputDisabled = disabled || letter.disabled || isAnimating;
                
                const expecificInputClassName = {
                    [classes.clue]: letter.isClue,
                    [classes.disabled]: inputDisabled,
                    [classes.semitransparent]: variant === VARIANT_INPUT.TRANSPARENT && letter.value
                }
                
                return (
                    <input
                        disabled={inputDisabled}
                        className={clsx(classNameInput,expecificInputClassName)}
                        type="text"
                        autoCapitalize="off"
                        autoCorrect="off"
                        autoComplete="off"
                        key={index}
                        ref={(element) => inputElements.current[index] = element}
                        onFocus={() => handleFocus(index)}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                        onPaste={(event) => onPaste(event, index)}
                        value={letter.value}
                        onChange={(event) => handleOnChange(event, index)}
                        onMouseEnter={() => gameSounds.play(NAME_SOUND_HOVER)}   
                    />
                )
            })}
        </div>
    )
}


PinInput.propTypes = {
    size: PropTypes.oneOf(['default', 'small']).isRequired,
    variant: PropTypes.oneOf(['transparent', 'default']).isRequired,
    variantValidation: PropTypes.oneOf(['correct', 'incorrect']),
    letters: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
    onNavigateNext: PropTypes.func.isRequired,
    onNavigatePrevious: PropTypes.func.isRequired,
    onNavigateDeletePrevious: PropTypes.func.isRequired,
    onPaste: PropTypes.func.isRequired,
    className: PropTypes.string,

}